// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// RxJS
import { Observable } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models
import { PatientModel } from '../_models/patient.model';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';
import { cloneDeep, isEqual, uniqWith } from 'lodash-es';

@Injectable()
export class PatientsService {
  private authToken;
  private company_id;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.company_id = res.companyID));
  }

  private static getPatientsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;

    let fromString = ``;
    if (!filter.isActive && sortField !== 'isActive') fromString = `sort=isActive,desc&`;
    fromString += `page=${pageNumber}&size=${pageSize}${
      sortField.localeCompare('name') === 0
        ? `&sort=firstName,${sortOrder}&sort=middleName,${sortOrder}&sort=lastName,${sortOrder}&sort=lastName2,${sortOrder}`
        : sortField.localeCompare('facility') === 0
        ? `&sort=facilityName,${sortOrder}`
        : `&sort=${sortField},${sortOrder}`
    }`;

    filter.doctors?.forEach((doctorId) => (fromString += `&doctor_id=${doctorId}`));
    filter.patients?.forEach((patientId) => (fromString += `&id=${patientId}`));
    filter.facilities?.forEach((facilityId) => (fromString += `&facility_id=${facilityId}`));

    if (filter.searchTerm) {
      fromString += `&search_term=${encodeURIComponent(filter.searchTerm)}&is_active=${
        filter.isActive
      }`;
    } else {
      if (filter.entityId) fromString += `&entity_id_has=${filter.entityId}`;
      if (filter.nameEmail)
        fromString += /^[^\s@]*@[^\s@]*$/.test(encodeURIComponent(filter.nameEmail))
          ? `&email_has=${encodeURIComponent(filter.nameEmail)}`
          : `&name_has=${encodeURIComponent(filter.nameEmail)}`;
      if (filter.dob) fromString += `&dob=${filter.dob}`;
      if (filter.cellphone) fromString += `&cell_phone_has=${encodeURIComponent(filter.cellphone)}`;
      if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
      if (filter.isHoldInHospital)
        fromString +=
          filter.isHoldInHospital.localeCompare('hold') === 0
            ? '&in_hold=true'
            : '&in_hospital=true';
    }

    return fromString;
  }

  // CREATE =>  POST: add a new patient to the server
  createPatient(patient: PatientModel): Observable<PatientModel> {
    // Note: Add headers if needed (tokens/bearer)

    const url = environment.CORE_BASE + `/companies/${this.company_id}/patients`;
    const body = cloneDeep(patient);
    delete body.id;
    // delete body.patient_id;
    delete body.location_id;

    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<PatientModel>(url, body, { headers: httpHeaders });
  }

  // READ
  getAllPatients(): Observable<PatientModel[]> {
    const url = environment.CORE_BASE + '/all-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<PatientModel[]>(url, { headers: httpHeaders });
  }

  getPatientByIds(patientIds) {
    let ids = '';
    patientIds = uniqWith(patientIds, isEqual);
    patientIds.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    const url = environment.CORE_BASE + `/companies/${this.company_id}/patients?page=0&size=10`;
    return this.http.get<any>(url, { params });
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result

  findPatients(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: PatientsService.getPatientsListQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.company_id}/patients/list-view`,
      { params },
    );
  }

  getPatientsAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const params = new HttpParams({
      fromString: PatientsService.getPatientsListQueryString(queryParams),
    });

    return this.http.get(`${environment.CORE_BASE}/companies/${this.company_id}/patients/csv`, {
      params,
      responseType: 'text',
    });
  }
  getDoctorsPatientsAsCSV(queryParams: QueryParamsModel, doctorID): Observable<any> {
    const params = new HttpParams({
      fromString: PatientsService.getPatientsListQueryString(queryParams),
    });
    return this.http.get(
      `${environment.CORE_BASE}/companies/${this.company_id}/patients/csv?sort=entityId,desc&doctor_id=${doctorID}&is_active=true&header=Facility_Name&header=Patient_Name&header=Active_Prescriptions_Total&header=DOB&header=On_Hold&header=In_Hospital&header=Status`,
      { params, responseType: 'text' },
    );
  }
  getFacilitiesPatientsAsCSV(queryParams: QueryParamsModel, facilityID): Observable<any> {
    const params = new HttpParams({
      fromString: PatientsService.getPatientsListQueryString(queryParams),
    });
    return this.http.get(
      `${environment.CORE_BASE}/companies/${this.company_id}/patients/csv?sort=entityId,desc&facility_id=${facilityID}&is_active=true&search_term=&header=Knostos_ID&header=Patient_ID&header=Patient&header=DOB&header=Cellphone&header=On_Hold&header=In_Hospital&header=Status`,
      { params, responseType: 'text' },
    );
  }

  findPatientsByFacility(facility_id, search_term): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      `/companies/${this.company_id}/patients?` +
      'facility_id=' +
      facility_id +
      '&search_term=' +
      search_term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchPatients(search_term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.company_id +
      '/patients?sort=firstName,asc&search_term=' +
      search_term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }
  searchPatientCareMonitor(search_term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.ME_BASE +
      '/companies/' +
      this.company_id +
      '/patients/autocomplete?term=' +
      search_term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the patient on the server
  updatePatient(patient: PatientModel): Observable<any> {
    const httpHeader = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + `/companies/${this.company_id}/patients/${patient.id}`;
    const body = patient;
    return this.http.put(url, body, { headers: httpHeader });
  }

  // UPDATE Status
  updateStatusForPatient(id: number, isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      is_active: isActive,
    };
    const url = environment.CORE_BASE + `/companies/${this.company_id}/entities/${id}/status`;
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForPatients(ids: number[], isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids,
      isActive,
    };
    const url = environment.CORE_BASE + '/patientstatusupdate';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the patient from the server
  deletePatient(patientId: number): Observable<any> {
    const url = environment.CORE_BASE + `/companies/${this.company_id}/patients/${patientId}`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  deletePatients(patientsIds: number[] = []): Observable<any> {
    const url = environment.CORE_BASE + '/multiple-soft-delete-patient';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: patientsIds,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  searchFacilities(term: string): Observable<any> {
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.company_id +
      '/entities/autocomplete?entityType=FACILITY&term=' +
      term;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchSpecies(term: string): Observable<any> {
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.company_id +
      '/species/autocomplete?term=' +
      term;
    return this.http.get<any>(url);
  }
  getFacility(id: number): Observable<any> {
    const url = environment.CORE_BASE + `/companies/${this.company_id}/facilities/${id}`;
    // Note: Add headers if needed (tokens/bearer)
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getPatientDevices(patientId: number): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = `${environment.ME_BASE}/companies/${this.company_id}/patient_vitals/${patientId}`;
    return this.http.get<any>(url, { headers });
  }

  uploadCSV(formData) {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.post<any>(
      environment.CORE_BASE + '/companies/' + this.company_id + '/patients/importCSV',
      formData,
      { headers },
    );
  }

  uploadinsuranseCSV(formData) {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.post<any>(
      environment.CORE_BASE + '/companies/' + this.company_id + '/patients/insurance/importCSV',
      formData,
      { headers },
    );
  }

  updatePatientImage(patientFile: File, pid): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.company_id}/entities/${pid}/attachment`;
    return this.http.post(url, { avatar: patientFile.name }, { headers });
  }

  uploadPatientImage(s3, patientFile: File): Observable<any> {
    const url = s3.data.url;
    return this.http.put(url, patientFile, {});
  }

  getPatientHistory(patientId: number, version?: number) {
    const patientUrl = `${environment.CORE_BASE}/companies/${this.company_id}/patients/${patientId}`;
    let patientHistoryUrl;
    if (version) {
      patientHistoryUrl =
        version > 0
          ? `${patientUrl}/history?versions=${version - 1}&versions=${version}`
          : `${patientUrl}/history?versions=${version}`;
    } else {
      patientHistoryUrl = `${patientUrl}/history?list=true`;
    }
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(patientHistoryUrl, { headers });
  }
}
